import React, { useEffect, useRef, useState } from 'react'
import { bool, number } from 'prop-types'
import styled from 'styled-components'

import PromoButton from './PromoButton'

const VideoLooperEl = styled.div`
  position: relative;
  video {
    vertical-align: bottom;
  }
`

const VideoLooperInner = styled.div`
  position: relative;
  height: 0;
  overflow: hidden;
  width: 100%;
  padding-bottom: ${(16 / 9) * 100}%;
  & > * {
    width: 100%;
  }
`

const VideoLooperAction = styled.div`
  position: absolute;
  top: 4px;
  right: 35px;
`

const VideoLooperProgress = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.2);
`

const VideoLooperProgressInner = styled.div`
  height: 4px;
  transition: width 0.25s linear;
  background-color: ${(props) => props.theme.colors.blue};
`

const videoStateData = {
  loading: {},
  canplay: { actionText: 'Play', actionIcon: 'play' },
  play: { actionText: 'Pause', actionIcon: 'pause' },
  pause: { actionText: 'Play', actionIcon: 'play' },
  ended: { actionText: 'Restart', actionIcon: 'replay' }
}

const VideoLooper = ({ ratio, showProgress, smallAction, ...other }) => {
  const [videoState, updateVideoState] = useState('loading')
  const [progress, updateProgress] = useState(0)

  const videoRef = useRef(null)

  const handleTogglePlayState = () => {
    if (videoRef.current.paused) {
      updateVideoState('play')
      videoRef.current.play()
    } else {
      updateVideoState('pause')
      videoRef.current.pause()
    }
  }

  const handleTimeUpdate = () => {
    if (!videoRef || !videoRef.current) return
    const progress =
      (videoRef.current.currentTime / videoRef.current.duration) * 100
    updateProgress(progress)
  }

  useEffect(() => {
    const video = videoRef.current
    const handleUpdatePlayState = (e) => {
      updateVideoState(e.type)
    }

    video.addEventListener('canplay', handleUpdatePlayState, { once: true })
    video.addEventListener('play', handleUpdatePlayState)
    video.addEventListener('pause', handleUpdatePlayState)
    video.addEventListener('ended', handleUpdatePlayState)

    return () => {
      video.removeEventListener('play', handleUpdatePlayState)
      video.removeEventListener('pause', handleUpdatePlayState)
      video.removeEventListener('ended', handleUpdatePlayState)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.addEventListener('timeupdate', handleTimeUpdate)
    }
  }, [])

  const { actionText, actionIcon } = videoStateData[videoState]

  return (
    <VideoLooperEl>
      <VideoLooperInner>
        <video ref={videoRef} {...other} playsInline />
      </VideoLooperInner>

      <VideoLooperAction>
        <PromoButton
          onClick={videoState !== 'loading' ? handleTogglePlayState : undefined}
          label={actionText}
        >
          {videoState === 'loading' && <p>…</p>}
          {actionIcon === 'play' && (
            <svg viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
          {actionIcon === 'pause' && (
            <svg viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          )}
          {actionIcon === 'replay' && (
            <svg viewBox="0 0 24 24">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
            </svg>
          )}
        </PromoButton>
      </VideoLooperAction>

      <VideoLooperProgress>
        <VideoLooperProgressInner style={{ width: `${progress}%` }} />
      </VideoLooperProgress>
    </VideoLooperEl>
  )
}

VideoLooper.propTypes = {
  ratio: number,
  showProgress: bool,
  smallAction: bool
}

export default VideoLooper
